import { useEffect, useState } from "react";
import Select from "../../../components/common/select/Select";
import TitleUpdater from "../../../components/common/title/TitleUpdater";
import styles from "./CreateProduct.module.scss";
import Button from "../../../components/common/button/Button";
import {
  BrandsTypes,
  CategoriesTypes,
  CreateProductType,
  CurrencyTypes,
  ErrorsType,
} from "../../../api/types/products.types";
import ProductsService from "../../../api/services/products.service";
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll";
import NestedSelect from "../../../components/common/nested-select/NestedSelect";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

const CreateProduct = () => {
  const { t } = useTranslation("translation");
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState<Partial<CreateProductType>>({});
  const [errors, setErrors] = useState<Partial<ErrorsType>>({});
  const navigate = useNavigate();
  const [currency, setCurrency] = useState<CurrencyTypes[]>([]);
  const { data: categories, loaderRef } = useInfiniteScroll<CategoriesTypes>({
    fetchFn: async (page) => {
      const res = await ProductsService.getCategories(page, 20);
      return res.data;
    },
    pageSize: 20,
  });
  const { data: brands, loaderRef: brandsRef } = useInfiniteScroll<BrandsTypes>(
    {
      fetchFn: async (page) => {
        const res = await ProductsService.getBrands(page, 20);
        return res.data.data;
      },
      pageSize: 20,
    },
  );

  useEffect(() => {
    const getCurrency = async () => {
      const res = await ProductsService.getCurrency();
      setCurrency(res.data);
    };
    getCurrency();
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Partial<ErrorsType> = {};
    // if (!formValues.brand_id) newErrors.brand_id = t("required.brand") || "";
    if (!formValues.category_id)
      newErrors.category_id = t("required.category") || "";
    if (!formValues.currency_id)
      newErrors.currency_id = t("required.currency") || "";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setLoading(true);
      const formData = new FormData();
      if (formValues.brand_id) {
        formData.append("brand_id", String(formValues.brand_id));
      }
      if (formValues.currency_id) {
        formData.append("currency_id", String(formValues.currency_id));
      }
      if (formValues.category_id) {
        formData.append("category_id", String(formValues.category_id));
      }
      const res = await ProductsService.createProduct(formData);
      const id = res.data.id;
      navigate(`/dashboard/products/create-variants/${id}`, {
        state: { category_id: res.data.category_id },
      });
      toast.success(t("products_details.data_saved"), {
        description: t("products_details.data_saved_catalog"),
      });
      setErrors({});
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t("error_message");
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof CreateProductType, value: any) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <TitleUpdater title={t("create_product")} />
      <div className={styles.grid}>
        <div className={styles.left}>
          <div className={styles.form_category}>
            <p className={styles.title}>{t("products_details.specs")}</p>
            <div className={styles.form_content_column}>
              <NestedSelect
                label={t("columns.category") || ""}
                categories={categories}
                value={formValues.category_id || null}
                onChange={(value) => handleChange("category_id", value)}
                error={errors.category_id}
                className={styles.input}
                loaderRef={loaderRef}
                language={i18n.language}
              />
              <Select
                label={t("columns.currency") || ""}
                value={formValues.currency_id?.toString() || ""}
                onChange={(val) => {
                  if (val) {
                    handleChange("currency_id", val.id);
                  }
                }}
                options={currency.map((b) => ({
                  id: String(b.id),
                  name: b.name,
                }))}
                className={styles.input}
                error={errors.currency_id}
              />
              <Select
                label={t("columns.brand") || ""}
                value={formValues.brand_id?.toString() || ""}
                onChange={(val) => {
                  if (val) {
                    handleChange("brand_id", val.id);
                  }
                }}
                options={brands.map((b) => ({
                  id: String(b.id),
                  name: b.name,
                }))}
                className={styles.input}
                loaderRef={brandsRef}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={styles.button}>
        <Button type="submit" disabled={loading}>
          {loading ? t("buttons.loading") : t("submit")}
        </Button>
      </div>
    </form>
  );
};

export default CreateProduct;
